import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Headphones, Search, Smartphone, X, MonitorUp } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useI18n } from '../../lib/i18n';
import { useMyDepartmentsLoaded } from '../../lib/useMyDepartment';
import { browserName } from '../../lib/share/peer';
import type { TargetRow, TargetsResponse } from '../../lib/share/types';
import { Avatar } from '../Avatar';
import { noticeText, useShare } from './ShareProvider';

/**
 * "Share your screen with…": buscador + grupos (mi departamento default, mis otros departamentos,
 * Everyone, y "Field team" —los inspectores— al final; spec §4.9), dentro de cada grupo primero los
 * que están en el app. MULTI-selección (spec §14): se marcan varios y el botón "Share with N" abre
 * el picker nativo en ESE clic (el gesto no sobrevive a esperar) e invita a todos al mismo broadcast.
 * En modo 'add' (agregar a un broadcast en curso) el título es "Add people", se excluye a quien ya
 * está mirando, y NO hay getDisplayMedia (se reusa la captura). ↑↓ para mover, Espacio/Enter para
 * marcar, Esc cierra. El inspector se pinta con su estado de campo ("On a job") y la pista del teléfono.
 */

type Group = { key: string; label: string; rows: TargetRow[] };

function deptLabel(key: string, t: (k: string) => string): string {
  const k = `dept.${key}`;
  const v = t(k);
  if (v !== k) return v;
  return key.charAt(0).toUpperCase() + key.slice(1);
}

function sortRows(rows: TargetRow[]): TargetRow[] {
  const rank = (r: TargetRow) => (r.busy ? 3 : r.appOpen && r.humanRecent ? 0 : r.appOpen ? 1 : 2);
  return [...rows].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
}

/** ¿Es del equipo de campo? Por el grupo que manda el servidor; el rol es el respaldo para filas viejas. */
const isField = (r: TargetRow) => r.group === 'field' || (r.group == null && r.role === 'inspector');

export function ShareTargetPicker() {
  const { t } = useI18n();
  const s = useShare();
  const { depts } = useMyDepartmentsLoaded();
  const [rows, setRows] = useState<TargetRow[] | null>(null);
  const [myDepts, setMyDepts] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const mode = s.pickerMode;
  // En modo 'add' se excluye a quien ya está invitado/mirando el broadcast (no re-invitar).
  const alreadyIn = useMemo(() => new Set(
    mode === 'add'
      ? (s.broadcast?.participants ?? [])
        .filter((p) => p.status === 'pending' || p.status === 'accepted' || p.status === 'live')
        .map((p) => p.viewer.id)
      : [],
  ), [mode, s.broadcast]);

  useEffect(() => {
    let alive = true;
    api.get<TargetsResponse>('/api/share/targets')
      .then((r) => { if (alive) { setRows(r.rows); setMyDepts(r.myDepartments ?? []); } })
      .catch((e) => {
        if (!alive) return;
        setRows([]);
        // Nunca el código crudo del servidor ('share_not_enabled', 'http_503') en la UI bilingüe.
        const code = e instanceof ApiError ? e.code : null;
        setErr(code === 'share_disabled' || code === 'share_not_enabled' ? t('share.notEnabledMe') : t('share.error'));
      });
    inputRef.current?.focus();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const groups = useMemo<Group[]>(() => {
    if (!rows) return [];
    const needle = q.trim().toLowerCase();
    const base = alreadyIn.size ? rows.filter((r) => !alreadyIn.has(r.id)) : rows;
    const filtered = needle ? base.filter((r) => r.name.toLowerCase().includes(needle)) : base;
    const mine = myDepts.length ? myDepts : depts.map((d) => d.key);
    const out: Group[] = [];
    const used = new Set<string>();
    // El equipo de campo va aparte y al final: nunca se mezcla con un departamento de oficina.
    const field = filtered.filter((r) => isField(r));
    field.forEach((r) => used.add(r.id));
    for (const key of mine) {
      const g = filtered.filter((r) => r.department === key && !used.has(r.id));
      if (!g.length) continue;
      g.forEach((r) => used.add(r.id));
      const named = depts.find((d) => d.key === key)?.name;
      out.push({ key, label: named ?? deptLabel(key, t), rows: sortRows(g) });
    }
    const rest = filtered.filter((r) => !used.has(r.id));
    if (rest.length) out.push({ key: 'everyone', label: t('share.everyone'), rows: sortRows(rest) });
    if (field.length) out.push({ key: 'field', label: t('share.fieldTeam'), rows: sortRows(field) });
    return out;
  }, [depts, myDepts, q, rows, t]);

  const flat = useMemo(() => groups.flatMap((g) => g.rows), [groups]);
  useEffect(() => { setCursor(0); }, [q, rows]);

  const now = Date.now();
  const disabledRow = (r: TargetRow) => !!r.busy || s.cooldownUntil(r.id) > now;

  /** Marca/desmarca una fila (multi-selección). Las deshabilitadas (ocupadas / en cooldown) no entran. */
  const toggle = (r: TargetRow) => {
    if (disabledRow(r)) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(r.id)) next.delete(r.id); else next.add(r.id);
      return next;
    });
  };

  /** Confirma: en 'start' el primero arranca la captura (dentro del gesto) y el resto se agrega; en
   *  'add' se agregan todos al broadcast en curso. El ORDEN es el de la lista pintada. */
  const confirm = () => {
    const chosen = flat.filter((r) => selected.has(r.id) && !disabledRow(r));
    if (!chosen.length) return;
    if (mode === 'add') s.addViewers(chosen);
    else s.startShare(chosen[0]!, chosen.slice(1));
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); s.closePicker(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(flat.length - 1, c + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(0, c - 1)); }
    else if (e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); const r = flat[cursor]; if (r) toggle(r); }
    else if (e.key === 'Enter') { e.preventDefault(); if (selected.size) confirm(); else { const r = flat[cursor]; if (r) toggle(r); } }
  };

  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${cursor}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  const status = (r: TargetRow) => {
    if (r.busy === 'sharing') return { cls: 'busy', text: t('share.busySharing') };
    if (r.busy === 'viewing') return { cls: 'busy', text: t('share.busyViewing') };
    // Inspector con la cita de hoy en curso: se le puede compartir igual, pero conviene saberlo.
    if (r.onJob) return { cls: 'job', text: t('share.onJob') };
    if (r.appOpen && r.humanRecent) return { cls: 'in', text: t('share.inApp') };
    if (r.appOpen) return { cls: 'idle', text: t('share.idle') };
    return { cls: 'out', text: t('share.notInApp') };
  };

  let idx = -1;
  const title = mode === 'add' ? t('share.addPeople') : t('share.pickTitle');
  const n = selected.size;
  // Con UNA sola persona (el 1:1 de siempre, el caso más común) el botón dice su NOMBRE, no "…con 1":
  // el flujo 1:1 se ve igual que antes. "…con N" queda solo para 2+.
  const soloName = n === 1 ? flat.find((r) => selected.has(r.id))?.name : undefined;

  return (
    <div className="ash-modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) s.closePicker(); }}>
      <div className="ash-modal ss-picker" role="dialog" aria-modal="true" aria-label={title} onKeyDown={onKey}>
        <div className="ash-modal-head">
          <b style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><MonitorUp size={17} strokeWidth={1.9} />{title}</b>
          <button type="button" className="btn ghost sm ash-open" onClick={s.closePicker} aria-label={t('common.close')}><X size={15} strokeWidth={1.75} /></button>
        </div>

        {s.macPerm ? (
          <div className="ss-macperm">
            <b>{t('share.macPermTitle')}</b>
            <p>{t('share.macPermBody', { browser: browserName(navigator.userAgent) ?? t('share.yourBrowser') })}</p>
            <button type="button" className="btn sm" onClick={() => { /* nuevo gesto: vuelve al selector */ s.openPicker(); }}>{t('share.tryAgain')}</button>
          </div>
        ) : (
          <>
            <div className="ss-search">
              <Search size={15} strokeWidth={2} className="ss-search-ico" />
              <input ref={inputRef} className="input sm ss-search-input" placeholder={t('share.search')} value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <p className="ss-hint">{mode === 'add' ? t('share.addHint') : t('share.pickHint')}</p>
            <p className="ss-hint soft"><Headphones size={13} strokeWidth={2} aria-hidden /> {t('share.headphones')}</p>
            {s.notice && <div className="ss-inline-notice">{noticeText(s.notice, t)}</div>}
            <div className="ss-list" ref={listRef}>
              {rows === null && <div className="ss-empty">{t('common.loading')}</div>}
              {rows !== null && err && <div className="ss-empty">{err}</div>}
              {rows !== null && !err && !flat.length && <div className="ss-empty">{t('share.nobody')}</div>}
              {groups.map((g) => (
                <div key={g.key} className="ss-group">
                  <div className="ss-group-label">{g.label}</div>
                  {g.rows.map((r) => {
                    idx += 1;
                    const i = idx;
                    const st = status(r);
                    const dis = disabledRow(r);
                    const sel = selected.has(r.id);
                    return (
                      <button
                        type="button"
                        key={r.id}
                        data-idx={i}
                        role="checkbox"
                        aria-checked={sel}
                        className={`ss-row${i === cursor ? ' is-cursor' : ''}${dis ? ' is-disabled' : ''}${sel ? ' is-selected' : ''}`}
                        onMouseEnter={() => setCursor(i)}
                        onClick={() => toggle(r)}
                        disabled={dis}
                        title={s.cooldownUntil(r.id) > now ? t('share.declined', { name: r.name }) : undefined}
                      >
                        <span className={`ss-check${sel ? ' on' : ''}`} aria-hidden>{sel && <Check size={13} strokeWidth={2.5} />}</span>
                        <Avatar userId={r.id} name={r.name} size={22} noCall />
                        <span className="ss-row-main">
                          <span className="ss-row-name">{r.name}</span>
                          <span className="ss-row-sub">{r.role}{r.department && !isField(r) ? ` · ${deptLabel(r.department, t)}` : ''}</span>
                          {r.phone && <span className="ss-row-hint"><Smartphone size={11} strokeWidth={2} aria-hidden style={{ verticalAlign: 'text-bottom' }} /> {t('share.phoneHint')}</span>}
                        </span>
                        <span className={`ss-status ${st.cls}`}><i /> {st.text}</span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
            <div className="ss-picker-foot">
              <button type="button" className="btn ghost sm" onClick={s.closePicker}>{t('share.cancel')}</button>
              <button type="button" className="btn sm ss-picker-go" onClick={confirm} disabled={n === 0}>
                {mode === 'add'
                  ? (soloName ? t('share.addOne', { name: soloName }) : t('share.addN', { n }))
                  : (soloName ? t('share.shareWith', { name: soloName }) : t('share.shareWithN', { n }))}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
